import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  gdprApi,
  GDPRConsentType,
  GDPRConsentMethod,
  type GDPRConsentSnapshot,
} from "../services/api/gdpr.api";

const CONSENT_QUERY_KEY = ["gdpr", "consent"];

/**
 * Текст согласия на обработку данных внешним AI-процессором (ADR-008 §5).
 * Фиксируется в записи согласия как доказуемый consentText (GDPR Art. 7).
 */
export const AI_CONSENT_TEXT =
  "Я разрешаю обработку моих данных внешним AI-провайдером (OpenAI) " +
  "для работы AI-функций. Данные передаются после редактирования PII " +
  "и без хранения на стороне провайдера (zero-retention).";

/**
 * Чтение + управление согласием `aiProcessing` для FE enforcement-guard'ов.
 *
 * Источник правды — backend GET /gdpr/consent (fail-closed: пока статус не
 * загружен или не дан, AI-фичи считаются недоступными). Грант фиксируется
 * через POST /gdpr/consent; версию Terms проставляет backend server-side.
 */
export const useAiConsent = () => {
  const queryClient = useQueryClient();

  const {
    data: snapshot,
    isLoading,
    isError,
    refetch,
  } = useQuery<GDPRConsentSnapshot>(
    CONSENT_QUERY_KEY,
    () => gdprApi.getConsents(),
    {
      staleTime: 5 * 60 * 1000, // согласие меняется редко
      retry: 1,
    },
  );

  const grantMutation = useMutation(
    () =>
      gdprApi.recordConsent({
        consentType: GDPRConsentType.AI_PROCESSING,
        given: true,
        consentMethod: GDPRConsentMethod.BUTTON,
        consentText: AI_CONSENT_TEXT,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CONSENT_QUERY_KEY);
      },
    },
  );

  const aiConsent = snapshot?.aiProcessing;

  return {
    /** Дано ли согласие на AI-обработку (fail-closed default false). */
    hasAiConsent: aiConsent?.given === true,
    /** Идёт первичная загрузка статуса согласия. */
    isLoading,
    /** Не удалось прочитать статус — guard трактует это как «нет согласия». */
    isError,
    /** Зафиксировать согласие на AI-обработку. */
    grantAiConsent: grantMutation.mutateAsync,
    isGranting: grantMutation.isLoading,
    grantError: grantMutation.error as Error | null,
    refetch,
  };
};

export default useAiConsent;
